import emptyImage from "@/assets/images/EmptyScreenSummary.png";
import { ParticipantTypeEnum } from "@/enums/RecapEnums";
import useResponsive from "@/hooks/useResponsive.hook";
import { AttendanceRow, EngagementSegment } from "@/interfaces/IRecap";
import {
  formatDurationText,
  formatSecondsToTime,
  formatTime,
  getTimestampInSeconds,
} from "@/middleware/Date";
import { Empty, Person } from "@nexus/shared-ui";
import { Col, Flex, Image, List, Row, Tooltip, Typography } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import RecapAbsentsPresentsSkeleton from "../../skeleton/RecapAbsentsPresentsSkeleton";
import AttendanceTag from "../AttendanceTag";

const { Text } = Typography;

const Attendance = ({
  participants,
  isLoading = false,
  currentSession,
  activeFilters = [],
}: {
  participants?: any;
  isLoading?: boolean;
  currentSession?: any;
  activeFilters?: ParticipantTypeEnum[];
}) => {
  const { t } = useTranslation();
  const isResponsive = useResponsive();

  const sessionStartSeconds = useMemo(
    () => getTimestampInSeconds(currentSession?.start || null),
    [currentSession?.start]
  );

  const sessionEndSeconds = useMemo(
    () => getTimestampInSeconds(currentSession?.end || null),
    [currentSession?.end]
  );

  const sessionDurationSeconds = useMemo(
    () => sessionEndSeconds - sessionStartSeconds,
    [sessionEndSeconds, sessionStartSeconds]
  );

  const getUserImage = useMemo(
    () => (userId?: string) => {
      if (!userId) return "";

      return `${(
        window._env_?.VITE_BASE_API_URL || import.meta.env.VITE_BASE_API_URL
      ).replace("/meet/", "/admincenter/")}/v1/users/${userId}/image`;
    },
    []
  );

  const getAttendanceTagValue = (type?: ParticipantTypeEnum) => {
    switch (type) {
      case ParticipantTypeEnum.Absent:
        return ParticipantTypeEnum.Absent;

      case ParticipantTypeEnum.JoinViaCall:
        return ParticipantTypeEnum.JoinViaCall;

      case ParticipantTypeEnum.Guest:
        return ParticipantTypeEnum.Guest;

      case ParticipantTypeEnum.Attendee:
      default:
        return ParticipantTypeEnum.Attendee;
    }
  };

  const rows: AttendanceRow[] = useMemo(() => {
    if (!participants?.data?.length) return [];

    return participants.data.map(
      (participant: any, participantIndex: number) => {
        const participantType = participant?.type as ParticipantTypeEnum;
        const isAbsent = participantType === ParticipantTypeEnum.Absent;

        const presences = participant?.presences || [];
        const firstPresence = presences[0];
        const lastPresence = presences[presences.length - 1];

        const segments: EngagementSegment[] = isAbsent
          ? []
          : (participant.statistics || []).map((stat: any, index: number) => {
              const startSeconds = getTimestampInSeconds(stat?.start);
              const endSeconds = stat?.end
                ? getTimestampInSeconds(stat.end)
                : sessionEndSeconds;

              const durationSeconds = endSeconds - startSeconds;
              const hasValidDuration =
                durationSeconds > 0 && sessionDurationSeconds > 0;

              const startPositionPercentage = hasValidDuration
                ? ((startSeconds - sessionStartSeconds) /
                    sessionDurationSeconds) *
                  100
                : 0;

              const widthPercentage = hasValidDuration
                ? (durationSeconds / sessionDurationSeconds) * 100
                : 0;

              const relativeStartSeconds = startSeconds - sessionStartSeconds;
              const relativeEndSeconds = endSeconds - sessionStartSeconds;

              const tooltipContent = `\u200E${formatSecondsToTime(
                relativeStartSeconds
              )} - ${formatSecondsToTime(relativeEndSeconds)}\u200E`;

              return {
                id: index,
                position: {
                  insetInlineStart: startPositionPercentage,
                  width: widthPercentage,
                },
                tooltip: tooltipContent,
              };
            });

        const inMeetingDurationSeconds =
          firstPresence?.start && lastPresence?.end
            ? getTimestampInSeconds(lastPresence.end) -
              getTimestampInSeconds(firstPresence.start)
            : 0;

        return {
          key: `${participantType}-${participant?.userId || participant?.nickname || participantIndex}`,
          id: participant?.userId || participantIndex,
          userId: participant?.userId,
          type: isAbsent ? "absent" : "present",
          participantType,
          avatar: getUserImage(participant?.userId || participant?.user?.id),
          fullName:
            participant?.user?.fullName ||
            participant?.nickname ||
            t("general.unknown"),
          nickname: participant?.nickname,
          jobTitle: participant?.user?.jobTitle || "",
          firstJoin: firstPresence?.start
            ? formatTime(firstPresence.start)
            : "",
          lastLeave: lastPresence?.end ? formatTime(lastPresence.end) : "",
          inMeetingDuration: isAbsent
            ? ""
            : formatDurationText(
                inMeetingDurationSeconds > 0 ? inMeetingDurationSeconds : 0
              ),
          inMeetingSpeechTime: isAbsent
            ? ""
            : formatDurationText(
                participant?.speechTime > 0 ? participant.speechTime : 0
              ),
          segments,
        };
      }
    );
  }, [
    participants,
    sessionStartSeconds,
    sessionEndSeconds,
    sessionDurationSeconds,
    t,
  ]);

  const dataSource = useMemo(
    () =>
      rows.filter(
        (row) =>
          !activeFilters.length || activeFilters.includes(row?.participantType)
      ),
    [rows, activeFilters]
  );

  if (isLoading) {
    return <RecapAbsentsPresentsSkeleton />;
  }

  return (
    <div className="recap-attendance-wrapper">
      {dataSource.length === 0 ? (
        <Flex justify="center">
          <Empty
            title={t("recap.noParticipantFound")}
            icon={<Image src={emptyImage} preview={false} />}
          />
        </Flex>
      ) : isResponsive ? (
        <div>
          <Flex className="py-2" vertical gap={16}>
            {dataSource.map((record) => (
              <Flex
                className={
                  record?.participantType === ParticipantTypeEnum.Absent
                    ? "attendance-card attendance-card--absent p-4"
                    : "attendance-card p-4"
                }
                key={record.key}
                vertical
                gap={16}
              >
                <Person
                  title={record?.fullName}
                  thumbUrl={record?.avatar}
                  description={record?.jobTitle || null}
                  classes={
                    record?.participantType === ParticipantTypeEnum.Absent
                      ? {
                          title: "person-name--absent",
                          description: "person-desc--absent",
                        }
                      : undefined
                  }
                />

                {record?.participantType !== ParticipantTypeEnum.Absent && (
                  <>
                    <Flex className="participation-timeline mb-2">
                      <Flex className="participation-timeline-track">
                        {record?.segments?.length > 0 ? (
                          record.segments.map((seg) => (
                            <Tooltip
                              key={seg.id}
                              title={seg.tooltip}
                              placement="top"
                            >
                              <div
                                className="participation-timeline-bar"
                                style={{
                                  insetInlineStart: `${seg.position.insetInlineStart}%`,
                                  width: `${seg.position.width}%`,
                                }}
                              />
                            </Tooltip>
                          ))
                        ) : (
                          <div className="participation-timeline-bar" />
                        )}
                      </Flex>
                    </Flex>

                    <Flex vertical gap={16}>
                      <Flex justify="space-between" align="center">
                        <Text className="h5">{t("recap.firstJoin")}</Text>
                        <Text className="h5">
                          {record?.firstJoin ? record?.firstJoin : "-"}
                        </Text>
                      </Flex>

                      <Flex justify="space-between" align="center">
                        <Text className="h5">{t("recap.lastLeave")}</Text>
                        <Text className="h5">
                          {record?.lastLeave ? record?.lastLeave : "-"}
                        </Text>
                      </Flex>
                    </Flex>
                  </>
                )}
              </Flex>
            ))}
          </Flex>
        </div>
      ) : (
        <List
          itemLayout="vertical"
          split={false}
          dataSource={dataSource}
          renderItem={(record: AttendanceRow) => (
            <List.Item key={record.key} className="attendance-list-row-item">
              <Row align="middle">
                <Col
                  span={13}
                  className={
                    record?.participantType === ParticipantTypeEnum.Absent
                      ? "attendance-list-col--name attendance-list-col--absent"
                      : "attendance-list-col--name"
                  }
                >
                  <Person
                    title={record?.fullName}
                    thumbUrl={record?.avatar}
                    description={record?.jobTitle || null}
                    extra={
                      <AttendanceTag
                        value={getAttendanceTagValue(record?.participantType)}
                      />
                    }
                    classes={
                      record?.participantType === ParticipantTypeEnum.Absent
                        ? {
                            title: "person-name--absent ",
                            description: "person-desc--absent",
                          }
                        : undefined
                    }
                  />
                </Col>

                <Col span={11}>
                  <Row>
                    <Col span={4} className="attendance-list-col--metric">
                      <Flex vertical gap={6}>
                        <Text className="h8 " type="secondary">
                          {t("recap.firstJoin")}
                        </Text>
                        <Text>
                          {record?.firstJoin ? record?.firstJoin : "-"}
                        </Text>
                      </Flex>
                    </Col>

                    <Col span={4} className="attendance-list-col--metric">
                      <Flex vertical gap={6}>
                        <Text className="h8 " type="secondary">
                          {t("recap.lastLeave")}
                        </Text>
                        <Text>
                          {record?.lastLeave ? record?.lastLeave : "-"}
                        </Text>
                      </Flex>
                    </Col>

                    <Col span={8} className="attendance-list-col--metric">
                      <Flex vertical gap={6}>
                        <Text className="h8 " type="secondary">
                          {t("recap.inMeetingDuration")}
                        </Text>
                        <Text>
                          {record?.inMeetingDuration
                            ? record?.inMeetingDuration
                            : "-"}
                        </Text>
                      </Flex>
                    </Col>

                    <Col span={8} className="attendance-list-col--metric">
                      <Flex vertical gap={6}>
                        <Text className="h8 " type="secondary">
                          {t("recap.inMeetingSpeechTime")}
                        </Text>
                        <Text>
                          {record?.inMeetingSpeechTime
                            ? record?.inMeetingSpeechTime
                            : "-"}
                        </Text>
                      </Flex>
                    </Col>
                    <Col span={24} className="attendance-list-col--timeline">
                      <Flex className="participation-timeline">
                        <Flex className="participation-timeline-track">
                          {record?.participantType !==
                            ParticipantTypeEnum.Absent &&
                          record?.segments.length > 0 ? (
                            record.segments.map((seg) => (
                              <Tooltip
                                key={seg.id}
                                title={seg.tooltip}
                                placement="top"
                              >
                                <div
                                  className="participation-timeline-bar"
                                  style={{
                                    insetInlineStart: `${seg.position.insetInlineStart}%`,
                                    width: `${seg.position.width}%`,
                                  }}
                                />
                              </Tooltip>
                            ))
                          ) : (
                            <div className="participation-timeline-bar" />
                          )}
                        </Flex>
                      </Flex>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </List.Item>
          )}
        />
      )}
    </div>
  );
};

export default Attendance;
